"use client";

import { SessionContext } from "next-auth/react";
import { useState, useEffect, useContext } from "react";
import { useAuthDialogStore } from "@/stores/useAuthDialogStore";

export interface UseAuthGuardProps {
  signInFeatureSlug?: string;
  signInTitle?: string;
  signInDescription?: string;
}

const useAuthGuard = (props?: UseAuthGuardProps) => {
  const sessionContext = useContext(SessionContext);
  const status = sessionContext?.status ?? "unauthenticated";
  const [isLoading, setIsLoading] = useState(status === "loading");
  const [isHandleActionInProgress, setIsHandleActionInProgress] = useState(false);

  const { showAuthDialog } = useAuthDialogStore();

  const [modeType, setModeType] = useState<"sign-in" | "sign-up">("sign-in");

  // Derived state for authentication status
  const isAuthenticated = status === "authenticated";

  // Sync isLoading state with session status
  useEffect(() => {
    setIsLoading(status === "loading");
  }, [status]);

  const handleAction = (action?: () => void, signInModeType: "sign-in" | "sign-up" = "sign-in") => {
    console.log('useAuthGuard handleAction called:', { signInModeType, isAuthenticated });
    setIsHandleActionInProgress(true);
    setModeType(signInModeType);

    if (isAuthenticated) {
      console.log('User already authenticated, executing action directly');
      setIsLoading(true);
      if (action) {
        action();
      }
      setIsLoading(false);
    } else {
      console.log('User not authenticated, showing auth dialog');
      showAuthDialog({
        open: true,
        onAuthenticated: onAuthenticated.bind(this, action),
        modeType: signInModeType,
        signInFeatureSlug: props?.signInFeatureSlug,
        signInTitle: props?.signInTitle,
        signInDescription: props?.signInDescription,
      });
    }

    setIsHandleActionInProgress(false);
  };

  const onAuthenticated = async (action?: () => void) => {
    setIsLoading(true);
    if (action) {
      await action();
    }
    setIsLoading(false);
    useAuthDialogStore.getState().setShowDialog(false);
  };

  const isDisabled = status === "loading" || isLoading;

  return {
    handleAction,
    isLoading,
    isDisabled,
    isAuthenticated,
    isHandleActionInProgress,
  };
};

export default useAuthGuard;
